import {
	Box,
	Button,
	H1,
	HStack,
	Input,
	Link,
	Tab,
	Tabs,
	Tag,
	Text,
	VStack,
} from '@montel/montelpro-shared-components-v2';
import {
	GET_DUPLICATE_HUBSPOTID_ACCOUNTS,
	MARK_PRIMARY_ACCOUNT,
} from 'apiRoutes';
import { ConditionallyDisabled } from 'components/common/ConditionallyDisabled/ConditionallyDisabled';
import { MontelAccount } from 'domain/account/interfaces/MontelAccount';
import useAuthRoles from 'hooks/useAuthRoles';
import { useGetOnMount } from 'hooks/useGetOnMount';
import useSubmit, { POST } from 'hooks/useSubmit';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import styled from 'styled-components';

interface AccountGrouping {
	[key: string]: MontelAccount[];
}

const PAGE_SIZE = 5;

const isGroupResolved = (accounts: MontelAccount[]) =>
	accounts.filter((a) => a.isPrimary).length === 1;

const PrimaryAccountToolPage = () => {
	const { data, getData } = useGetOnMount<AccountGrouping>(
		GET_DUPLICATE_HUBSPOTID_ACCOUNTS,
	);

	const [activeTab, setActiveTab] = useState<string>('unresolved');
	const [searchQuery, setSearchQuery] = useState<string>('');
	const [currentPage, setCurrentPage] = useState<number>(1);

	const [postMarkPrimary] = useSubmit(MARK_PRIMARY_ACCOUNT, POST, {
		messageOnError: 'An error occured, please try again!',
		messageOnSuccess: undefined,
		showApiError: true,
	});

	const activeGroups = useMemo(() => {
		if (!data) return {};
		const unresolved: AccountGrouping = {};
		const resolved: AccountGrouping = {};
		Object.entries(data).forEach(([id, accounts]) => {
			(isGroupResolved(accounts) ? resolved : unresolved)[id] = accounts;
		});
		return activeTab === 'resolved'
			? resolved
			: activeTab === 'unresolved'
			? unresolved
			: data;
	}, [data, activeTab]);

	useEffect(() => {
		setCurrentPage(1);
	}, [searchQuery, activeTab]);

	const { isSuperAdmin, isMontelAccountAdmin } = useAuthRoles();
	const allowedToMarkPrimary = isSuperAdmin || isMontelAccountAdmin;

	const filteredGroups = Object.entries(activeGroups).filter(
		([hubspotId, accounts]) =>
			hubspotId.toLowerCase().includes(searchQuery.toLowerCase()) ||
			accounts.find(
				(a) =>
					(a.firstName + ' ' + a.lastName)
						.toLowerCase()
						.includes(searchQuery.toLowerCase()) ||
					a.email.value.includes(searchQuery.toLowerCase()),
			),
	);
	const totalPages = Math.max(
		1,
		Math.ceil(filteredGroups.length / PAGE_SIZE),
	);
	const pagedGroups = filteredGroups.slice(
		(currentPage - 1) * PAGE_SIZE,
		currentPage * PAGE_SIZE,
	);

	const handleMarkPrimary = async (account: MontelAccount) => {
		const result = await postMarkPrimary({ accountId: account.id });
		if (result.success) {
			toast.success(
				`${account.email.value} marked as primary for Hubspot ID: ${account.hubSpotId}`,
				{ autoClose: 2000 },
			);
			getData();
		}
	};

	return (
		<Box>
			<VStack margin="0 0 2rem 0">
				<H1>Primary account tool</H1>
				<Text color="textSubtle">
					This tool helps managing accounts that share the same
					Hubspot ID. Mark one account as primary for each group to
					ensure that it is that account that gets synced and updated
					in HubSpot for changes related to that Hubspot ID.
				</Text>
			</VStack>
			{data && (
				<VStack gap="xl">
					<StyledInput
						size="sm"
						label="Search by Hubspot ID"
						hideLabel
						placeholder="Search by Hubspot ID..."
						value={searchQuery}
						onChange={(e) => setSearchQuery(e.target.value)}
					/>
					<Tabs activeTab={activeTab} setActiveTab={setActiveTab}>
						<Tab label="Unresolved" value="unresolved" />
						<Tab label="Resolved" value="resolved" />
					</Tabs>
					<VStack gap="lg">
						{filteredGroups.length === 0 && (
							<HStack
								fullWidth
								justify="center"
								align="center"
								gap="md"
							>
								<Text
									fontStyle="italic"
									size="lg"
									color="textSubtle"
								>
									No accounts to show in this view
								</Text>
							</HStack>
						)}
						{pagedGroups.map(([hubspotId, accounts]) => (
							<AccountGroupCard
								key={hubspotId}
								hubspotId={hubspotId}
								accounts={accounts}
								onMarkPrimary={handleMarkPrimary}
								allowedToMarkPrimary={allowedToMarkPrimary}
							/>
						))}
						{filteredGroups.length > 0 && (
							<HStack
								fullWidth
								justify="center"
								align="center"
								gap="md"
							>
								<Button
									size="sm"
									text="Previous"
									variant="secondary"
									isDisabled={currentPage === 1}
									onClick={() => setCurrentPage((p) => p - 1)}
								/>
								<Text>{`${currentPage} / ${totalPages}`}</Text>
								<Button
									size="sm"
									text="Next"
									variant="secondary"
									isDisabled={currentPage === totalPages}
									onClick={() => setCurrentPage((p) => p + 1)}
								/>
							</HStack>
						)}
					</VStack>
				</VStack>
			)}
		</Box>
	);
};

export default PrimaryAccountToolPage;

interface AccountGroupCardProps {
	hubspotId: string;
	accounts: MontelAccount[];
	onMarkPrimary: (account: MontelAccount) => void;
	allowedToMarkPrimary: boolean;
}

const AccountGroupCard = ({
	hubspotId,
	accounts,
	onMarkPrimary,
	allowedToMarkPrimary,
}: AccountGroupCardProps) => {
	const groupResolved = isGroupResolved(accounts);

	return (
		<StyledBox
			fullWidth
			variant="outlined"
			bg={groupResolved ? 'bgSuccess' : 'borderDefault'}
			gap="lg"
		>
			<HStack
				fullWidth
				justify="space-between"
				align="center"
				margin="0 0 1rem 0"
			>
				<StyledUnderlinedHStack align="flex-end" gap="sm" fullWidth>
					<Text color="textSubtle">Hubspot ID:</Text>
					<Text>{hubspotId}</Text>
				</StyledUnderlinedHStack>
			</HStack>
			{accounts.map((account) => {
				const showMarkPrimaryButton =
					!groupResolved || (groupResolved && !account.isPrimary);

				return (
					<StyledHStack
						key={account.id}
						justify="space-between"
						align="center"
						fullWidth
						$highlight={groupResolved && account.isPrimary}
					>
						<HStack align="center" gap="lg">
							<Link href={`/accounts/users/${account.id}`}>
								{`${account.firstName} ${account.lastName} - ${account.email.value}`}
							</Link>
							{groupResolved && account.isPrimary && (
								<Tag variant="green" shape="square">
									Primary
								</Tag>
							)}
						</HStack>
						<StyledFixedWidthHStack justify="center" align="center">
							{showMarkPrimaryButton && (
								<ConditionallyDisabled
									disableIf={!allowedToMarkPrimary}
									tooltip="You do not have permission to perform this action."
								>
									<Button
										size="sm"
										text="Mark primary"
										variant="secondary"
										onClick={() => onMarkPrimary(account)}
									/>
								</ConditionallyDisabled>
							)}
						</StyledFixedWidthHStack>
					</StyledHStack>
				);
			})}
		</StyledBox>
	);
};

const StyledHStack = styled(HStack)<{ $highlight?: boolean }>`
	&:hover {
		background-color: ${({ theme }) => theme.colors.bgHover};
	}

	border-radius: 5px;
	padding: 5px;
`;

const StyledBox = styled(Box)`
	min-width: 800px;
`;

const StyledFixedWidthHStack = styled(HStack)`
	min-width: 120px;
`;

const StyledInput = styled(Input)`
	max-width: 300px;
`;

const StyledUnderlinedHStack = styled(HStack)`
	padding-bottom: 5px;
	border-bottom: 1px solid ${({ theme }) => theme.colors.borderDefault};
`;
